import React from 'react';
import { util } from 'veritone-redux-common';
import { last } from 'lodash';
import { connect } from 'react-redux';
import {
  getEngines,
  fetchEngine,
  getFailedFetchingEngines
} from 'modules/engine';
import { Link } from 'react-router';

import IconButton from '@material-ui/core/IconButton';
import Button from '@material-ui/core/Button';
import Popover from '@material-ui/core/Popover';
import List from '@material-ui/core/List';
import ListItemText from '@material-ui/core/ListItemText';

import SettingIcon from '@material-ui/icons/Settings';
import DropDownIcon from '@material-ui/icons/ArrowDropDown';
import BuildIcon from '@material-ui/icons/Build';

import rootSaga from '../modules/details-sagas';
import { PropTypes, injectInto } from 'helpers/react';
import styles from './styles/engineDetailsContainer.scss';
import withUIState from 'shared-components/withUIState';
import { Tabs, Tab } from 'shared-components/Tabs';
import withData from 'shared-components/withData';
import { Container, Row, Col } from 'shared-components/grid';
import FailureScreen from 'shared-components/FailureScreen';
import LabeledLozenge from 'shared-components/LabeledLozenge';
import FullScreenDialog from 'shared-components/FullScreenDialog';
import DocumentationLink from 'shared-components/DocumentationLink';

const { children, string, shape, routerShape } = PropTypes;
const saga = util.reactReduxSaga.saga;

@saga(rootSaga)
@withUIState({
  key: 'engine-details',
  defaultState: props => ({
    // put current engineId in state so sagas can reference it
    engineId: props.params.engineId
  })
})
@withData([
  {
    action: ownProps => fetchEngine(ownProps.params.engineId),
    blockPageLoad: true
  }
])
@connect((state, ownProps) => ({
  engine: getEngines(state)[ownProps.params.engineId],
  error: getFailedFetchingEngines(state).includes(ownProps.params.engineId)
    ? 'Engine failed to load.'
    : null
}))
export default class EngineDetailsContainer extends React.Component {
  static propTypes = {
    params: shape({ engineId: string }).isRequired,
    engine: shape({
      id: string,
      categoryId: string,
      category: shape({
        name: string
      }),
      description: string
    }).isRequired,
    error: string,
    children,
    route: shape({
      resource: string.isRequired
    }).isRequired,
    router: routerShape
  };

  static defaultProps = {
    engine: {}
  };

  state = {
    anchorEl: null
  };

  handleOpenGenerateChoice = event => {
    this.setState({
      anchorEl: event.currentTarget
    });
  };

  handleClose = () => {
    this.setState({
      anchorEl: null
    });
  };

  handlePageChange = (event, value) => {
    this.props.router.push(
      `/${this.props.route.resource}s/${this.props.router.params
        .engineId}/${value}`
    );
  };

  handleCloseConfigModal = () => {
    const engineId = this.props.params.engineId;
    this.props.router.push(`/engines/${engineId}`);
  };

  handleClickItem = () => {
    this.setState({
      anchorEl: null
    });
  };

  render() {
    const {
      engine,
      router,
      route: { resource, path },
      error,
      children
    } = this.props;
    const { anchorEl } = this.state;
    const activePage = last(router.routes).path;
    const isShowConfigWizard = path === ':engineId/config';
    return (
      <div>
        <Container topBarOffset sideBarOffset>
          <Row>
            <Col lg={12}>
              <div className={styles['header-container']}>
                <div className={styles['headerInfo']}>
                  {!!engine.iconPath &&
                    <img src={engine.iconPath} className={styles['icon']} />}
                  <div>
                    <div className={styles['header']}>
                      <div className={styles['headerInfo']}>
                        <h1 className={styles['name']}>
                          {engine.name}
                        </h1>
                        {resource === 'engine' &&
                          <Link
                            to={`/${resource}s/${router.params
                              .engineId}/config/details/functionality`}
                          >
                            <IconButton className={styles['settingButton']}>
                              <SettingIcon />
                            </IconButton>
                          </Link>}
                      </div>
                    </div>
                    <span className={styles['engineId']}>
                      <span className={styles['engineId__tittle']}>
                        Engine ID:
                      </span>{' '}
                      {engine.id}
                    </span>
                  </div>
                </div>
                <div className={styles['buttonGroup']}>
                  <Button
                    className={styles['generateButton']}
                    onClick={this.handleOpenGenerateChoice}
                  >
                    <BuildIcon className={styles['buildIcon']} />
                    ENGINE TOOLS
                    <DropDownIcon className={styles['dropDownIcon']} />
                  </Button>
                  <Popover
                    open={Boolean(anchorEl)}
                    anchorEl={anchorEl}
                    onClose={this.handleClose}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'right'
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'right'
                    }}
                  >
                    <List>
                      <ListItemText
                        primary="VTN Output Sample"
                        secondary="Generate a sample of the VTN output for this engine class."
                        className={styles['menuItem']}
                        onClick={this.handleClose}
                      />
                      <ListItemText
                        primary="Sample Task Request"
                        secondary="Generate a task request to compare to your engine."
                        className={styles['menuItem']}
                        onClick={this.handleClose}
                      />
                      <hr />
                      <div className={styles['engineContainer']}>
                        <a
                          href="https://machinebox.io/veritone/engine-toolkit"
                          target="_blank"
                          className={styles['linkWithoutDecoration']}
                        >
                          <ListItemText
                            primary="Download Veritone Engine Driver"
                            secondary="Use our SDK to test and validate your engine."
                            className={styles['menuItem']}
                            onClick={this.handleClose}
                          />
                        </a>
                        <div className={styles['engineDriver']}>
                          <ListItemText
                            className={styles['verTag']}
                            secondary="VERSION"
                            onClick={this.handleClose}
                          />
                          <ListItemText
                            className={styles['verNum']}
                            secondary="0.1.16"
                            onClick={this.handleClose}
                          />
                        </div>
                      </div>
                      <DocumentationLink
                        src="https://docs.veritone.com/#/developer/engines/testing-and-debugging/?id=data-validation-testing" // eslint-disable-line max-len
                        style={{
                          textDecoration: 'none'
                        }}
                      >
                        <ListItemText
                          primary="Validate Output"
                          secondary="Use the output validator to validate some output."
                          className={styles['menuItem']}
                          onClick={this.handleClose}
                        />
                      </DocumentationLink>
                    </List>
                  </Popover>
                </div>
              </div>
            </Col>
          </Row>
          <Row>
            {engine.state === 'active' &&
              <Col lg={12}>
                <LabeledLozenge
                  label="build"
                  value={engine.builds.count || 0}
                  color="dark"
                  format="int"
                />
                <LabeledLozenge
                  label={engine.state}
                  value={engine.modifiedDateTime}
                  color={engine.state}
                  format="dateTimeShort"
                />
              </Col>}
          </Row>
          <Row>
            <Col lg={12}>
              <Tabs value={activePage} onChange={this.handlePageChange}>
                <Tab label="Builds" value="builds" />
                <Tab label="Tasks" value="tasks" />
                {resource === 'adapter' &&
                  <Tab label="Configuration" value="configuration" />}
                {resource === 'adapter' &&
                  <Tab label="Custom Fields" value="custom-fields" />}
              </Tabs>
              <hr className={styles['divider']} />
            </Col>
          </Row>
          {error && <FailureScreen message={error} />}
          {!error &&
            (isShowConfigWizard
              ? <FullScreenDialog open={!!children}>
                  {injectInto(children, {
                    onClose: this.handleCloseConfigModal,
                    engine,
                    resource: resource
                  })}
                </FullScreenDialog>
              : <div>
                  {injectInto(children, {
                    engine,
                    resource: resource
                  })}
                </div>)}
        </Container>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/client/plugins/engines-old/components/EngineDetailsContainer.js